import { createSelector } from '@reduxjs/toolkit';
import lazyDeepCopy from 'admin/src/components/Pollworker/utils/lazyDeepCopy';
import { CustomFieldDataType } from 'admin/src/enums';
import type { AppState } from 'admin/src/store';
import type {
  CustomField,
  CustomFieldData,
  PollworkerApiResponse,
  PollworkerUserInfo,
  PollworkerWorkAssignmentConfirmationsForElection,
  PollworkerWorkHistory,
  Schedule,
} from 'admin/src/types';
import { getCustomFieldsByPromptTextKeysSelector, getCustomFieldsByPromptTextSelector, getReportsToByIdSelector } from './grid';

export * from './tracker';
export * from './staffing';
export * from './grid';
export * from './requirements';
export * from './courses';
export * from './availabilitySurvey';

export const selectedPollworkerModuleInfo = (state: AppState) => state.pollworker.moduleInfo;
export const getShowPollworkerDetailsId = (state: AppState) => state.pollworker.grid.showPollworkerDetailsId;
export const getPollworkerWorkAssignmentConfirmationForElection = (state: AppState) => state.pollworker.grid.pollworkerWorkAssignmentConfirmationsForElection;
export const getPollworkerCustomFieldsData = (state: AppState) => state.pollworker.grid.pollworkerCustomFieldsData;
export const getPollworkers = (state: AppState) => state.pollworker.grid.pollworkers;
export const getRoleTypes = (state: AppState) => state.pollworker.grid.roleTypes;
export const getModuleInfo = (state: AppState) => state.pollworker.moduleInfo;
export const getWorkDefaults = (state: AppState) => state.pollworker.workDefaults;

type CustomFieldValue = string | number | boolean | null;
type CustomFieldsMap = Record<string, CustomFieldValue>;
type CustomFieldsByWorkerId = Record<string, CustomFieldsMap>;

export const getRatifiedCustomFieldsByWorkerIdSelector = createSelector(
  [getCustomFieldsByPromptTextKeysSelector, getCustomFieldsByPromptTextSelector, getPollworkerCustomFieldsData],
  (promptTextKeys, customFieldsByPromptText, pollworkerCustomFieldsData): CustomFieldsByWorkerId => {
    return pollworkerCustomFieldsData.reduce((acc: CustomFieldsByWorkerId, pwCf: CustomFieldData) => {
      if (pwCf.ObjectType !== 'Pollworker') return acc;

      acc[pwCf.id] = promptTextKeys.reduce((fieldAcc: CustomFieldsMap, key: string) => {
        const schema = customFieldsByPromptText[key];
        const rawValue = pwCf[key];
        fieldAcc[key] = rawValue !== undefined ? rawValue : schema?.dataTypeId === CustomFieldDataType.YesNo ? false : null;
        return fieldAcc;
      }, {});

      return acc;
    }, {});
  },
);

function buildDefaultCustomFields(promptTextKeys: string[], customFieldsByPromptText: Record<string, CustomField>): CustomFieldsMap {
  return promptTextKeys.reduce((acc: CustomFieldsMap, key: string) => {
    acc[key] = customFieldsByPromptText[key]?.dataTypeId === CustomFieldDataType.YesNo ? false : null;
    return acc;
  }, {});
}

export const getHydratedCustomFieldsSelector = createSelector(
  [getRatifiedCustomFieldsByWorkerIdSelector, getCustomFieldsByPromptTextKeysSelector, getCustomFieldsByPromptTextSelector],
  (ratifiedByWorkerId, promptTextKeys, customFieldsByPromptText) => {
    const defaultFields = buildDefaultCustomFields(promptTextKeys, customFieldsByPromptText);
    return (lookupKey: string): CustomFieldsMap => {
      return ratifiedByWorkerId[lookupKey] ?? { ...defaultFields };
    };
  },
);

export const getPollworkerWorkAssignmentConfirmationsForElectionByPollworkerWorkHistoryIdSelector = createSelector(
  [getPollworkerWorkAssignmentConfirmationForElection],
  (pollworkerWorkAssignmentConfirmationsForElection) => {
    return pollworkerWorkAssignmentConfirmationsForElection.reduce(
      (acc: Record<string, PollworkerWorkAssignmentConfirmationsForElection>, confirmation: PollworkerWorkAssignmentConfirmationsForElection) => {
        acc[confirmation.pollworkerWorkHistoryId] = confirmation;
        return acc;
      },
      {},
    );
  },
);

export { getCustomFieldsByPromptTextKeysSelector, getCustomFieldsByPromptTextSelector };

export const getBasePollworkerSelector = createSelector(
  [getPollworkerWorkAssignmentConfirmationsForElectionByPollworkerWorkHistoryIdSelector, getReportsToByIdSelector, getPollworkers],
  (pwWorkAssignmentConfirmationForElectionByPollworkerId: { [key: string]: PollworkerWorkAssignmentConfirmationsForElection }, reportsToById, pw) => {
    const pollworkers = lazyDeepCopy<PollworkerApiResponse>(pw);
    return pollworkers.workerList.map((pollworker: PollworkerWorkHistory) => {
      const electionDayLocation = pw.locationList.find((location: Schedule) => pollworker.keyEVUserId === location.keyEVUserId);

      pollworker.electionDayRole = electionDayLocation?.roleInfo;
      pollworker.electionDayLocation = electionDayLocation?.votingLocationInfo;

      pollworker._reportsTo = reportsToById[pollworker.reportsToId] ?? ({} as PollworkerUserInfo);

      const workConfirmationForPollworker = pwWorkAssignmentConfirmationForElectionByPollworkerId[pollworker.id];

      pollworker._workAssignmentConfirmation = {
        ...workConfirmationForPollworker,
        value: workConfirmationForPollworker?.status,
      };

      pollworker.pollworkerInfo.registeredPrecinctInfo ||= {};

      return pollworker;
    });
  },
);

export const getPollworkerSelector = createSelector([getBasePollworkerSelector, getHydratedCustomFieldsSelector], (basePollworkers, getCustomFields) => {
  return basePollworkers.map((pollworker) => ({
    ...pollworker,
    _customFields: getCustomFields(pollworker.keyPollworkerId),
  }));
});

export const getAllWorkersSelector = createSelector([(state: AppState) => state.pollworker.grid.allWorkers, getHydratedCustomFieldsSelector], (allWorkers, getCustomFields) => {
  return allWorkers.map((worker) => ({
    ...worker,
    _customFields: getCustomFields(worker.id),
  }));
});

export const getShowPollworkerDetails = createSelector(
  [getBasePollworkerSelector, getAllWorkersSelector, getHydratedCustomFieldsSelector, getShowPollworkerDetailsId],
  (basePollworkers, allWorkers, getCustomFields, showPollworkerDetailsId) => {
    const electionWorker = basePollworkers.find((pw) => pw.id === showPollworkerDetailsId || pw.keyEVUserId === showPollworkerDetailsId);
    const worker = electionWorker || allWorkers.find((pw) => pw.id === showPollworkerDetailsId || pw.keyEVUserId === showPollworkerDetailsId);

    if (!worker) return undefined;

    const lookupKey = electionWorker ? worker.keyPollworkerId : worker.id;
    return { ...worker, _customFields: getCustomFields(lookupKey) };
  },
);
